// Design Proposal v3 — "Quiet Precision" / Linear-app inspired.
// Ultra-clean typography (Geist Sans + Geist Mono), monochrome
// neutrals with a single indigo accent, generous spacing, hairline
// dividers.  Pure styling preview — zero logic, zero API calls.
// Route: /styleguide-v3
import { Link } from "react-router-dom";
import {
  Calendar, Target, Repeat, History, Zap, Trophy, Clock, ChevronRight,
  Menu, Search, BarChart3, Activity, ArrowUp, ArrowDown, Circle,
} from "lucide-react";

const PALETTE = [
  { name: "Canvas",      hex: "#09090b", role: "App background" },
  { name: "Surface",     hex: "#111113", role: "Card surface" },
  { name: "Raised",      hex: "#17171a", role: "Elevated surface" },
  { name: "Line",        hex: "#1f1f23", role: "Hairline border" },
  { name: "Text",        hex: "#fafafa", role: "Primary text" },
  { name: "Text sub",    hex: "#71717a", role: "Secondary text" },
  { name: "Indigo",      hex: "#6366f1", role: "Accent · action" },
  { name: "Emerald",     hex: "#22c55e", role: "Positive" },
  { name: "Rose",        hex: "#f43f5e", role: "Negative" },
  { name: "Amber",       hex: "#eab308", role: "Warning" },
];

const NAV = [
  { label: "Schedule",  icon: Calendar, active: true },
  { label: "Live",      icon: Zap,      active: false },
  { label: "Autopilot", icon: Target,   active: false },
  { label: "Rollover",  icon: Repeat,   active: false },
  { label: "Accuracy",  icon: Trophy,   active: false },
];

const PICKS = [
  { home: "Sassuolo",  away: "Lazio",     league: "Serie A",   ko: "18:00",
    market: "HT BTTS",       pick: "No",           conf: 74.8, odds: 1.72, delta: +4.1 },
  { home: "AS Roma",   away: "Cagliari",  league: "Serie A",   ko: "20:45",
    market: "HT BTTS",       pick: "No",           conf: 81.3, odds: 1.65, delta: +2.6 },
  { home: "Nantes",    away: "Rennes",    league: "Ligue 1",   ko: "20:00",
    market: "Double Chance", pick: "Home or Draw", conf: 69.0, odds: 1.44, delta: -1.1 },
];

const TABLE_ROWS = [
  { t: "18:00:12", pair: "Sassuolo · Lazio",      mkt: "HT BTTS / No",      stake: 24.00, odds: 1.72, pnl: +16.72, state: "placed"  },
  { t: "18:02:48", pair: "Nantes · Rennes",       mkt: "DC / 1X",           stake: 24.00, odds: 1.44, pnl:   0.00, state: "placing" },
  { t: "17:51:03", pair: "Everton · Newcastle",   mkt: "O/U 2.5 / Under",   stake: 20.00, odds: 2.05, pnl: -20.00, state: "failed"  },
  { t: "17:40:15", pair: "AS Roma · Cagliari",    mkt: "HT BTTS / No",      stake: 24.00, odds: 1.65, pnl: +15.60, state: "settled" },
];

const STATE_STYLE = {
  placed:  "#a1a1aa",
  placing: "#eab308",
  settled: "#22c55e",
  failed:  "#f43f5e",
};

const TILES = [
  { label: "ROI 7d",    value: "+18.4%",  trend: +2.3,  pos: true  },
  { label: "Hit rate",  value: "63.2%",   trend: +1.1,  pos: true  },
  { label: "Avg odds",  value: "1.72",    trend: -0.03, pos: false },
  { label: "Open",      value: "7",       trend: null,  pos: null  },
];

const INTER_FALLBACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "SF Pro Text", system-ui, sans-serif';
const GEIST_SANS =
  '"Geist", "Geist Sans", "Inter Display", "Inter", ' + INTER_FALLBACK;
const GEIST_MONO =
  '"Geist Mono", "JetBrains Mono", "SF Mono", ui-monospace, monospace';

export default function StyleguideV3Page() {
  return (
    <div
      className="min-h-screen w-full antialiased"
      data-testid="styleguide-v3-page"
      style={{
        background: "#09090b",
        color: "#fafafa",
        fontFamily: GEIST_SANS,
        fontFeatureSettings: '"cv11", "ss01"',
      }}
    >
      {/* Fonts.  Geist from Vercel's CDN, with Inter Display fallback. */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        href="https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500;600&family=Inter+Tight:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      {/* ─── v1 / v2 / v3 toggle ─── */}
      <div className="w-full flex justify-center pt-5 pb-2">
        <div
          className="inline-flex items-center rounded-full p-1 text-xs"
          style={{
            background: "#111113",
            border: "1px solid #1f1f23",
          }}
          data-testid="styleguide-toggle"
        >
          <Link
            to="/styleguide"
            className="px-3.5 py-1.5 rounded-full transition"
            style={{ color: "#71717a" }}
            data-testid="styleguide-toggle-v1"
          >
            v1 · Amber
          </Link>
          <Link
            to="/styleguide-v2"
            className="px-3.5 py-1.5 rounded-full transition"
            style={{ color: "#71717a" }}
            data-testid="styleguide-toggle-v2"
          >
            v2 · Cyan
          </Link>
          <span
            className="px-3.5 py-1.5 rounded-full"
            style={{
              background: "#17171a",
              color: "#fafafa",
              border: "1px solid #27272a",
              fontWeight: 500,
            }}
            data-testid="styleguide-toggle-v3-current"
          >
            v3 · Quiet
          </span>
        </div>
      </div>

      {/* ─── Navigation (desktop) ─── */}
      <nav
        className="hidden md:flex items-center justify-between px-10 py-4 sticky top-0 z-10"
        style={{
          background: "rgba(9,9,11,0.72)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid #1f1f23",
        }}
      >
        <div className="flex items-center gap-10">
          <Logo />
          <div className="flex items-center gap-6">
            {NAV.map(({ label, icon: Icon, active }) => (
              <button
                key={label}
                className="relative flex items-center gap-1.5 text-sm transition"
                style={{
                  color: active ? "#fafafa" : "#71717a",
                  fontWeight: active ? 500 : 400,
                  letterSpacing: "-0.005em",
                }}
              >
                <Icon className="h-[14px] w-[14px]" strokeWidth={1.75} />
                <span>{label}</span>
                {active && (
                  <span
                    className="absolute -bottom-[18px] left-0 right-0 h-px"
                    style={{ background: "#fafafa" }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="h-8 w-8 rounded-md flex items-center justify-center transition"
            style={{ background: "#111113", border: "1px solid #1f1f23" }}
          >
            <Search className="h-3.5 w-3.5" style={{ color: "#71717a" }} strokeWidth={1.75} />
          </button>
          <div
            className="px-3 py-1.5 rounded-md text-xs flex items-center gap-2"
            style={{
              background: "transparent",
              border: "1px solid #1f1f23",
              color: "#71717a",
              fontFamily: GEIST_MONO,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <Circle className="h-1.5 w-1.5" fill="#22c55e" stroke="#22c55e" />
            <span style={{ color: "#fafafa" }}>$1,243.80</span>
          </div>
          <div
            className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium"
            style={{
              background: "#fafafa",
              color: "#09090b",
              letterSpacing: "-0.02em",
            }}
          >
            NK
          </div>
        </div>
      </nav>

      {/* Mobile nav */}
      <nav
        className="md:hidden flex items-center justify-between px-4 py-3.5"
        style={{
          background: "rgba(9,9,11,0.75)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid #1f1f23",
        }}
      >
        <button className="p-2 -ml-2">
          <Menu className="h-4 w-4" style={{ color: "#fafafa" }} strokeWidth={1.75} />
        </button>
        <Logo compact />
        <div
          className="h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-medium"
          style={{
            background: "#fafafa",
            color: "#09090b",
            letterSpacing: "-0.02em",
          }}
        >
          NK
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-5 md:px-10 py-12 md:py-16 pb-24 space-y-20">
        {/* ─── Hero ─── */}
        <section>
          <div
            className="text-[11px] uppercase inline-flex items-center gap-2"
            style={{
              color: "#71717a",
              letterSpacing: "0.14em",
              fontFamily: GEIST_MONO,
            }}
          >
            <span
              className="h-1 w-1 rounded-full"
              style={{ background: "#6366f1" }}
            />
            Design Proposal · v3
          </div>
          <h1
            className="mt-4 text-4xl md:text-6xl leading-[1.02]"
            style={{
              fontWeight: 500,
              letterSpacing: "-0.045em",
            }}
          >
            Quiet, modern,<br />
            <span style={{ color: "#71717a" }}>built around the font.</span>
          </h1>
          <p
            className="mt-6 max-w-xl text-[15px] leading-relaxed"
            style={{
              color: "#a1a1aa",
              fontWeight: 400,
              letterSpacing: "-0.005em",
            }}
          >
            Near-black canvas, hairline dividers, generous whitespace.
            Geist Sans for every text and Geist Mono for every number.
            A single indigo accent on the few things that take an action —
            everything else is quiet, so the data reads first.
          </p>
        </section>

        {/* ─── Palette ─── */}
        <section>
          <SectionTitle number="01" title="Palette" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 mt-8">
            {PALETTE.map((c) => (
              <div
                key={c.name}
                className="rounded-md p-3 transition hover:border-zinc-700 duration-150"
                style={{ background: "#111113", border: "1px solid #1f1f23" }}
              >
                <div
                  className="h-14 w-full rounded-sm mb-3"
                  style={{
                    background: c.hex,
                    border: "1px solid rgba(255,255,255,0.03)",
                  }}
                />
                <div
                  className="text-[13px]"
                  style={{ fontWeight: 500, letterSpacing: "-0.01em" }}
                >
                  {c.name}
                </div>
                <div
                  className="text-[11px] mt-0.5"
                  style={{
                    color: "#71717a",
                    fontFamily: GEIST_MONO,
                  }}
                >
                  {c.hex}
                </div>
                <div
                  className="text-[11px] mt-1"
                  style={{ color: "#71717a" }}
                >
                  {c.role}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Typography ─── */}
        <section>
          <SectionTitle number="02" title="Typography" />
          <div
            className="rounded-lg p-7 md:p-10 mt-8 space-y-8"
            style={{ background: "#111113", border: "1px solid #1f1f23" }}
          >
            <TypeRow
              hint='Display · Geist 56 / -5%'
              render={
                <div
                  className="text-5xl md:text-6xl"
                  style={{
                    fontWeight: 500,
                    letterSpacing: "-0.05em",
                  }}
                >
                  Hit rate 63.2%
                </div>
              }
            />
            <TypeRow
              hint='Heading · Geist 24 / -2.5%'
              render={
                <div
                  className="text-2xl"
                  style={{
                    fontWeight: 500,
                    letterSpacing: "-0.025em",
                  }}
                >
                  Top 20 Elite — tonight
                </div>
              }
            />
            <TypeRow
              hint='Body · Geist 15'
              render={
                <div
                  className="text-[15px] max-w-xl leading-relaxed"
                  style={{ color: "#d4d4d8", letterSpacing: "-0.005em" }}
                >
                  Light tracking, generous leading. The copy becomes
                  readable at any size without feeling bulky. Everything
                  the eye lands on is calm — nothing is shouting.
                </div>
              }
            />
            <TypeRow
              hint='Numeric · Geist Mono 20 · tabular'
              render={
                <div
                  className="text-xl flex gap-8 items-end"
                  style={{
                    fontFamily: GEIST_MONO,
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: "-0.01em",
                  }}
                >
                  <span style={{ color: "#22c55e" }}>+18.42%</span>
                  <span style={{ color: "#3f3f46" }}>|</span>
                  <span>1.72</span>
                  <span style={{ color: "#3f3f46" }}>|</span>
                  <span style={{ color: "#f43f5e" }}>−20.00</span>
                </div>
              }
            />
          </div>
        </section>

        {/* ─── Match / pick card ─── */}
        <section>
          <SectionTitle number="03" title="Match card · pick row" />
          <div className="grid gap-2 mt-8">
            {PICKS.map((p) => (
              <PickCard key={`${p.home}-${p.away}`} p={p} />
            ))}
          </div>
          <div
            className="text-[12px] mt-4 leading-relaxed"
            style={{ color: "#71717a" }}
          >
            One hairline, zero shadows. Everything that isn't data fades
            to the background. The pick pill is the only coloured object
            in the row — your eye finds it instantly.
          </div>
        </section>

        {/* ─── Data table ─── */}
        <section>
          <SectionTitle number="04" title="Data table · recent bets" />
          <div
            className="rounded-lg mt-8 overflow-hidden"
            style={{ background: "#111113", border: "1px solid #1f1f23" }}
          >
            <table
              className="w-full text-[13px]"
              style={{
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <thead>
                <tr
                  style={{
                    color: "#71717a",
                    borderBottom: "1px solid #1f1f23",
                  }}
                  className="text-[11px] uppercase tracking-wider text-left"
                >
                  <th className="px-4 py-3 font-normal" style={{ fontFamily: GEIST_MONO }}>Time</th>
                  <th className="px-4 py-3 font-normal">Match</th>
                  <th className="px-4 py-3 font-normal">Market</th>
                  <th className="px-4 py-3 font-normal text-right">Stake</th>
                  <th className="px-4 py-3 font-normal text-right">Odds</th>
                  <th className="px-4 py-3 font-normal text-right">P&amp;L</th>
                  <th className="px-4 py-3 font-normal text-right">State</th>
                </tr>
              </thead>
              <tbody>
                {TABLE_ROWS.map((r, i) => {
                  const stateColor = STATE_STYLE[r.state];
                  const pnlPos = r.pnl > 0;
                  const pnlZero = r.pnl === 0;
                  return (
                    <tr
                      key={i}
                      className="transition"
                      style={{
                        borderBottom: i === TABLE_ROWS.length - 1 ? "none" : "1px solid #1f1f23",
                      }}
                    >
                      <td
                        className="px-4 py-3.5"
                        style={{
                          color: "#71717a",
                          fontFamily: GEIST_MONO,
                        }}
                      >
                        {r.t}
                      </td>
                      <td
                        className="px-4 py-3.5"
                        style={{ color: "#fafafa", fontWeight: 450 }}
                      >
                        {r.pair}
                      </td>
                      <td
                        className="px-4 py-3.5"
                        style={{ color: "#a1a1aa" }}
                      >
                        {r.mkt}
                      </td>
                      <td
                        className="px-4 py-3.5 text-right"
                        style={{ fontFamily: GEIST_MONO }}
                      >
                        ${r.stake.toFixed(2)}
                      </td>
                      <td
                        className="px-4 py-3.5 text-right"
                        style={{ fontFamily: GEIST_MONO }}
                      >
                        {r.odds.toFixed(2)}
                      </td>
                      <td
                        className="px-4 py-3.5 text-right"
                        style={{
                          color: pnlZero ? "#71717a" : (pnlPos ? "#22c55e" : "#f43f5e"),
                          fontFamily: GEIST_MONO,
                        }}
                      >
                        {pnlZero ? "±0.00" : (pnlPos ? "+" : "") + r.pnl.toFixed(2)}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <span
                          className="inline-flex items-center gap-1.5 text-[11px]"
                          style={{ color: stateColor, letterSpacing: "0.01em" }}
                        >
                          <Circle className="h-1.5 w-1.5" fill={stateColor} stroke={stateColor} />
                          {r.state}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* ─── Stat tiles ─── */}
        <section>
          <SectionTitle number="05" title="Metric tiles · dashboard" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mt-8">
            {TILES.map((t) => (
              <StatTile key={t.label} t={t} />
            ))}
          </div>
        </section>

        {/* ─── Call to action ─── */}
        <section
          className="rounded-lg p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
          style={{
            background: "#111113",
            border: "1px solid #1f1f23",
          }}
        >
          <div>
            <div
              className="text-[11px] uppercase tracking-widest"
              style={{ color: "#6366f1", fontFamily: GEIST_MONO }}
            >
              Ready
            </div>
            <div
              className="mt-2 text-[17px]"
              style={{
                fontWeight: 500,
                letterSpacing: "-0.015em",
                color: "#fafafa",
              }}
            >
              Approve v3 and I'll roll the Schedule page over to this look.
            </div>
          </div>
          <div className="flex items-center gap-2 text-[12px]">
            <Link
              to="/styleguide"
              className="px-3.5 py-2 rounded-md transition hover:text-zinc-200"
              style={{
                border: "1px solid #1f1f23",
                color: "#71717a",
              }}
              data-testid="styleguide-footer-view-v1"
            >
              v1
            </Link>
            <Link
              to="/styleguide-v2"
              className="px-3.5 py-2 rounded-md transition hover:text-zinc-200"
              style={{
                border: "1px solid #1f1f23",
                color: "#71717a",
              }}
              data-testid="styleguide-footer-view-v2"
            >
              v2
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

/* ────────── Local sub-components ────────── */

function SectionTitle({ number, title }) {
  return (
    <div className="flex items-baseline gap-5">
      <span
        className="text-[11px]"
        style={{
          color: "#52525b",
          fontFamily: GEIST_MONO,
        }}
      >
        {number}
      </span>
      <h2
        className="text-xl"
        style={{
          fontWeight: 500,
          letterSpacing: "-0.025em",
        }}
      >
        {title}
      </h2>
      <div
        className="flex-1 h-px"
        style={{ background: "#1f1f23" }}
      />
    </div>
  );
}

function TypeRow({ hint, render }) {
  return (
    <div>
      <div
        className="text-[11px] mb-2 uppercase tracking-wider"
        style={{
          color: "#52525b",
          fontFamily: GEIST_MONO,
        }}
      >
        {hint}
      </div>
      {render}
    </div>
  );
}

function PickCard({ p }) {
  const confHigh = p.conf >= 75;
  const confMid = p.conf >= 65 && p.conf < 75;
  const confColor = confHigh ? "#22c55e" : confMid ? "#6366f1" : "#eab308";
  const deltaPos = p.delta > 0;
  return (
    <div
      className="group relative rounded-lg transition hover:bg-zinc-900/40 duration-150"
      style={{ background: "#111113", border: "1px solid #1f1f23" }}
    >
      <div className="grid grid-cols-12 items-center gap-4 px-5 py-4">
        {/* LEFT · kickoff + league */}
        <div className="col-span-12 md:col-span-3 flex items-center gap-3">
          <div
            className="flex items-center justify-center h-10 w-10 rounded-md shrink-0"
            style={{
              background: "#17171a",
              border: "1px solid #1f1f23",
            }}
          >
            <Clock className="h-3.5 w-3.5" style={{ color: "#71717a" }} strokeWidth={1.75} />
          </div>
          <div>
            <div
              className="text-[17px] leading-none"
              style={{
                fontFamily: GEIST_MONO,
                fontWeight: 500,
                fontVariantNumeric: "tabular-nums",
                letterSpacing: "-0.01em",
              }}
            >
              {p.ko}
            </div>
            <div
              className="text-[12px] mt-1.5"
              style={{ color: "#71717a" }}
            >
              {p.league}
            </div>
          </div>
        </div>

        {/* MIDDLE · match + market pill */}
        <div className="col-span-12 md:col-span-6">
          <div
            className="text-[15px]"
            style={{ fontWeight: 500, letterSpacing: "-0.01em" }}
          >
            {p.home} <span style={{ color: "#52525b", fontWeight: 400 }}>vs</span> {p.away}
          </div>
          <div className="mt-1.5 flex items-center gap-2 text-[12px]">
            <span style={{ color: "#71717a" }}>{p.market}</span>
            <span style={{ color: "#3f3f46" }}>·</span>
            <span
              className="px-2 py-0.5 rounded"
              style={{
                background: "rgba(99,102,241,0.1)",
                border: "1px solid rgba(99,102,241,0.3)",
                color: "#a5a3f7",
                fontWeight: 500,
                letterSpacing: "0.005em",
              }}
            >
              {p.pick}
            </span>
          </div>
        </div>

        {/* RIGHT · confidence + odds + delta */}
        <div className="col-span-12 md:col-span-3 flex items-center justify-between md:justify-end gap-5">
          <div className="flex items-center gap-2.5">
            <div
              className="h-[3px] w-20 rounded-full overflow-hidden"
              style={{ background: "#1f1f23" }}
            >
              <div
                className="h-full rounded-full"
                style={{ width: `${p.conf}%`, background: confColor }}
              />
            </div>
            <div
              className="text-[12px] w-10 text-right"
              style={{
                fontFamily: GEIST_MONO,
                fontVariantNumeric: "tabular-nums",
                color: confColor,
              }}
            >
              {p.conf.toFixed(1)}
            </div>
          </div>
          <div className="flex items-center gap-3 text-right">
            <div>
              <div
                className="text-[16px] leading-none"
                style={{
                  fontFamily: GEIST_MONO,
                  fontWeight: 500,
                  fontVariantNumeric: "tabular-nums",
                  letterSpacing: "-0.01em",
                }}
              >
                {p.odds.toFixed(2)}
              </div>
              <div
                className="text-[11px] mt-1.5 flex items-center gap-0.5 justify-end"
                style={{
                  color: deltaPos ? "#22c55e" : "#f43f5e",
                  fontFamily: GEIST_MONO,
                }}
              >
                {deltaPos ? <ArrowUp className="h-2.5 w-2.5" strokeWidth={2.5} /> : <ArrowDown className="h-2.5 w-2.5" strokeWidth={2.5} />}
                {deltaPos ? "+" : ""}{p.delta.toFixed(1)}%
              </div>
            </div>
            <ChevronRight
              className="h-3.5 w-3.5 hidden md:block transition group-hover:translate-x-0.5"
              style={{ color: "#3f3f46" }}
              strokeWidth={1.75}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatTile({ t }) {
  const trendPos = t.trend != null && t.trend > 0;
  const trendColor = t.trend == null ? "#71717a" : (trendPos ? "#22c55e" : "#f43f5e");
  return (
    <div
      className="rounded-lg px-5 py-5 transition hover:border-zinc-700 duration-150"
      style={{ background: "#111113", border: "1px solid #1f1f23" }}
    >
      <div
        className="text-[11px] uppercase tracking-wider"
        style={{
          color: "#71717a",
          letterSpacing: "0.08em",
        }}
      >
        {t.label}
      </div>
      <div
        className="mt-3 text-3xl"
        style={{
          fontFamily: GEIST_MONO,
          fontWeight: 500,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.03em",
        }}
      >
        {t.value}
      </div>
      {t.trend != null ? (
        <div
          className="mt-1.5 text-[11px] flex items-center gap-1"
          style={{
            color: trendColor,
            fontFamily: GEIST_MONO,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {trendPos ? <ArrowUp className="h-2.5 w-2.5" strokeWidth={2.5} /> : <ArrowDown className="h-2.5 w-2.5" strokeWidth={2.5} />}
          {trendPos ? "+" : ""}{t.trend}
          <span style={{ color: "#52525b", marginLeft: 2 }}>· 7d</span>
        </div>
      ) : (
        <div
          className="mt-1.5 text-[11px]"
          style={{ color: "#52525b" }}
        >
          live
        </div>
      )}
    </div>
  );
}

function Logo({ compact = false }) {
  return (
    <div className="flex items-center gap-2.5">
      <div
        className="relative h-5 w-5 rounded-sm flex items-center justify-center"
        style={{ background: "#fafafa" }}
      >
        <BarChart3 className="h-3 w-3" style={{ color: "#09090b" }} strokeWidth={2.25} />
      </div>
      {!compact && (
        <div
          className="text-[15px]"
          style={{
            fontWeight: 600,
            letterSpacing: "-0.025em",
          }}
        >
          Ndakubet
        </div>
      )}
    </div>
  );
}
